/**
 * The group pop-up's filter (19/10/2026): All · Today. "Today" is the row's
 * set — the same lists that carry the "Today" badge — in today's send order;
 * All keeps the group's order and still counts a list of today's set the
 * group's rule no longer matches. "Not today" is gone (23/10/2026, client QA:
 * "Remove").
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanGroupRow } from '@trafficpulse/shared';

import type { ListGroupResolveRow } from '@/features/list-groups';

import { listName } from './today-list-order';

export type ListScope = 'all' | 'today';

export interface ScopedList {
  wiserId: number;
  name: string | null;
  leadCount: number | null;
  today: boolean;
}

type ListRef = { wiserId: number; name: string | null };

/** Today's set as the row's badges read it — send order (boardListRows sorts it). */
export const todaySetOf = (group: Pick<DayPlanGroupRow, 'listsToday' | 'listOfDay'>): ListRef[] =>
  group.listsToday ?? (group.listOfDay ? [group.listOfDay] : []);

const scoped = (list: ListRef, row: ListGroupResolveRow | undefined, today: boolean): ScopedList => ({
  wiserId: list.wiserId,
  name: row?.name ?? list.name,
  leadCount: row?.leadCount ?? null,
  today,
});

export function scopeLists(lists: ListGroupResolveRow[], today: ListRef[]): Record<ListScope, ScopedList[]> {
  const byId = new Map(lists.map((row) => [row.wiserId, row]));
  const todayIds = new Set(today.map((list) => list.wiserId));
  const todayRows = today.map((list) => scoped(list, byId.get(list.wiserId), true));
  const outsideRule = todayRows.filter((row) => !byId.has(row.wiserId));
  return { all: [...lists.map((row) => scoped(row, row, todayIds.has(row.wiserId))), ...outsideRule], today: todayRows };
}

export function searchLists(rows: ScopedList[], query: string): ScopedList[] {
  const needle = query.trim().toLowerCase();
  return needle ? rows.filter((row) => listName(row).toLowerCase().includes(needle)) : rows;
}

/** The search in every filter at once — the pills count the matches, so a hit in another filter shows. */
export const searchScopes = (scopes: Record<ListScope, ScopedList[]>, query: string): Record<ListScope, ScopedList[]> => ({
  all: searchLists(scopes.all, query),
  today: searchLists(scopes.today, query),
});

/** What an empty filter says, in words. */
export function emptyListsText(scope: ListScope, groupName: string, query: string): string {
  const q = query.trim();
  if (q) return scope === 'today' ? `No list of today matches “${q}”.` : `No list name matches “${q}”.`;
  if (scope === 'today') return `No list of ${groupName} is used today.`;
  return 'This group has no matching lists.';
}

export const defaultScope = (today: ListRef[]): ListScope => (today.length ? 'today' : 'all');
